import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, router } from '@inertiajs/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { toast } from 'sonner'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form'
import { Toaster } from '../../components/ui/sonner'
import { Logo } from '../../components/logo'

const forgotPasswordSchema = z.object({
  email: z.string().email(),
})

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>

interface ForgotPasswordProps {
  flash?: {
    success?: string
    error?: string
  }
}

export default function ForgotPassword({ flash }: ForgotPasswordProps) {
  const { t } = useTranslation('auth')
  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  // Show flash messages as toasts
  useEffect(() => {
    if (flash?.success) {
      toast.success(flash.success)
    }
    if (flash?.error) {
      toast.error(flash.error)
    }
  }, [flash])

  const onSubmit = (data: ForgotPasswordForm) => {
    router.post('/password_resets', data)
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Toaster />

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo className="h-12" />
        </div>

        <Card className="w-full shadow-none">
          <CardHeader>
            <CardTitle>{t('forgotPassword.title')}</CardTitle>
            <CardDescription>
              {t('forgotPassword.subtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('forgotPassword.email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t('forgotPassword.emailPlaceholder')}
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('forgotPassword.rememberPassword')}{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              {t('forgotPassword.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
